/**
 * Reviewable maneuver insight thresholds and wording.
 */

import type { BetterDirection } from '../types';

export interface ManeuverMetricRule {
  id: string;
  metric: string;
  label: string;
  unit: string;
  threshold: number;
  better: BetterDirection;
  headlineLoss: string;
  headlineGain: string;
  recommendation: string;
}

/**
 * Candidate drivers that explain high total loss.
 * Phases first (where the metres went), then execution / trim at accmax.
 * Loss is the sailor-facing metric — do not pair with VMG% (same gap, different units).
 */
export interface ManeuverDriverRule {
  id: string;
  metric: string;
  label: string;
  unit: string;
  threshold: number;
  /**
   * Directional compare vs leaders. Omit when `matchLeaders` is true
   * (trim/setup where matching leaders matters more than higher/lower).
   */
  better?: BetterDirection;
  /**
   * When true, any gap beyond threshold vs leaders counts as a driver while
   * explaining extra loss (cant / CA1 / twist / jib at max accel).
   */
  matchLeaders?: boolean;
  /** Short cause when we are worse — used in "Driven by …". */
  causeLoss: string;
  /** Short cause when we are better. */
  causeGain: string;
  recommendation: string;
}

/** Outcome cards use loss only (VMG% is the same gap in different units). */
export const MANEUVER_TOTAL_RULES: ManeuverMetricRule[] = [
  {
    id: 'loss_total',
    metric: 'Loss_total_tgt',
    label: 'Total loss vs target',
    unit: 'm',
    threshold: 5,
    better: 'lower',
    headlineLoss: 'Losing more metres per maneuver than the leaders',
    headlineGain: 'Losing fewer metres per maneuver than the leaders',
    recommendation:
      'Cut total loss toward the leaders\' {value} average ({leaders}).',
  },
];

/** Phase + execution drivers used to explain why an outcome metric is off. */
export const MANEUVER_DRIVER_RULES: ManeuverDriverRule[] = [
  {
    id: 'loss_inv',
    metric: 'Loss_inv_tgt',
    label: 'Investment-phase loss',
    unit: 'm',
    threshold: 3,
    better: 'lower',
    causeLoss: 'investment-phase loss',
    causeGain: 'cleaner investment phase',
    recommendation:
      'Tighten the entry / investment — leaders average {value} inv loss ({leaders}).',
  },
  {
    id: 'loss_turn',
    metric: 'Loss_turn_tgt',
    label: 'Turn-phase loss',
    unit: 'm',
    threshold: 3,
    better: 'lower',
    causeLoss: 'turn-phase loss',
    causeGain: 'cleaner turn phase',
    recommendation:
      'Tighten the turn — leaders average {value} turn loss ({leaders}).',
  },
  {
    id: 'loss_build',
    metric: 'Loss_build_tgt',
    label: 'Build-phase loss',
    unit: 'm',
    threshold: 3,
    better: 'lower',
    causeLoss: 'build / accel-phase loss',
    causeGain: 'cleaner build phase',
    recommendation:
      'Rebuild speed faster — leaders average {value} build loss ({leaders}).',
  },
  {
    id: 'bsp_min_delta',
    metric: 'Bsp_min_delta',
    label: 'BSP drop through turn',
    unit: 'kph',
    threshold: 0.5,
    better: 'lower',
    causeLoss: 'deeper boat-speed bleed through the turn',
    causeGain: 'less boat-speed bleed through the turn',
    recommendation:
      'Protect boat speed through the turn — leaders average {value} BSP drop ({leaders}).',
  },
  {
    id: 'bsp_min',
    metric: 'Bsp_min',
    label: 'Minimum BSP',
    unit: 'kph',
    threshold: 0.5,
    better: 'higher',
    causeLoss: 'lower minimum boat speed',
    causeGain: 'higher minimum boat speed',
    recommendation:
      'Keep more boat speed at the bottom of the turn — leaders average {value} min BSP ({leaders}).',
  },
  {
    id: 'overshoot_perc',
    metric: 'Overshoot_perc',
    label: 'Overshoot %',
    unit: '%',
    threshold: 2,
    better: 'lower',
    causeLoss: 'more heading overshoot',
    causeGain: 'less heading overshoot',
    recommendation:
      'Reduce overshoot — leaders average {value} ({leaders}).',
  },
  {
    id: 'accel_slope',
    metric: 'Accel_slope',
    label: 'Acceleration slope',
    unit: '',
    threshold: 0.05,
    better: 'higher',
    causeLoss: 'slower acceleration rebuild',
    causeGain: 'faster acceleration rebuild',
    recommendation:
      'Rebuild faster after min BSP — leaders average accel slope {value} ({leaders}).',
  },
  {
    id: 'decel_slope',
    metric: 'Decel_slope',
    label: 'Deceleration slope',
    unit: '',
    threshold: 0.05,
    better: 'lower',
    causeLoss: 'harder deceleration into the turn',
    causeGain: 'softer deceleration into the turn',
    recommendation:
      'Ease the entry deceleration — leaders average decel slope {value} ({leaders}).',
  },
  {
    id: 'turn_radius',
    metric: 'Turn_radius',
    label: 'Turn radius',
    unit: 'm',
    threshold: 2,
    better: 'lower',
    causeLoss: 'wider turn radius',
    causeGain: 'tighter turn radius',
    recommendation:
      'Tighten the turn radius toward the leaders\' {value} average ({leaders}).',
  },
  {
    id: 'mmg',
    metric: 'Mmg',
    label: 'Metres made good',
    unit: 'm',
    threshold: 5,
    better: 'higher',
    causeLoss: 'fewer metres made good',
    causeGain: 'more metres made good',
    recommendation:
      'Match leaders\' MMG of {value} ({leaders}).',
  },
  // Accmax trim/setup — match leaders (direction is boat/mode specific).
  {
    id: 'cant_accmax',
    metric: 'Cant_accmax',
    label: 'Cant at max accel',
    unit: 'deg',
    threshold: 1.5,
    matchLeaders: true,
    causeLoss: 'different cant at max accel',
    causeGain: 'cant at max accel closer to leaders',
    recommendation:
      'Try leaders\' cant at peak accel: {value} ({leaders}).',
  },
  {
    id: 'wing_ca1_accmax',
    metric: 'Wing_ca1_accmax',
    label: 'Wing CA1 at max accel',
    unit: 'deg',
    threshold: 1.5,
    matchLeaders: true,
    causeLoss: 'different wing CA1 at max accel',
    causeGain: 'wing CA1 at max accel closer to leaders',
    recommendation:
      'Match leaders\' wing CA1 at peak accel: {value} ({leaders}).',
  },
  {
    id: 'wing_twist_accmax',
    metric: 'Wing_twist_accmax',
    label: 'Wing twist at max accel',
    unit: 'deg',
    threshold: 1.5,
    matchLeaders: true,
    causeLoss: 'different wing twist at max accel',
    causeGain: 'wing twist at max accel closer to leaders',
    recommendation:
      'Match leaders\' wing twist at peak accel: {value} ({leaders}).',
  },
  {
    id: 'jib_sheet_pct_accmax',
    metric: 'Jib_sheet_pct_accmax',
    label: 'Jib sheet % at max accel',
    unit: '%',
    threshold: 3,
    matchLeaders: true,
    causeLoss: 'different jib sheet % at max accel',
    causeGain: 'jib sheet % at max accel closer to leaders',
    recommendation:
      'Match leaders\' jib sheet % at peak accel: {value} ({leaders}).',
  },
  {
    id: 'jib_lead_ang_accmax',
    metric: 'Jib_lead_ang_accmax',
    label: 'Jib lead angle at max accel',
    unit: 'deg',
    threshold: 1.5,
    matchLeaders: true,
    causeLoss: 'different jib lead at max accel',
    causeGain: 'jib lead at max accel closer to leaders',
    recommendation:
      'Match leaders\' jib lead angle at peak accel: {value} ({leaders}).',
  },
  {
    id: 'jib_cunno_load_accmax',
    metric: 'Jib_cunno_load_accmax',
    label: 'Jib cunno load at max accel',
    unit: 'kgf',
    threshold: 50,
    matchLeaders: true,
    causeLoss: 'different jib cunno load at max accel',
    causeGain: 'jib cunno load at max accel closer to leaders',
    recommendation:
      'Match leaders\' jib cunno load at peak accel: {value} ({leaders}).',
  },
];

/** @deprecated Prefer MANEUVER_DRIVER_RULES phase entries — kept for tests/consumers. */
export const MANEUVER_PHASE_RULES: ManeuverMetricRule[] = MANEUVER_DRIVER_RULES
  .filter((d) => d.id.startsWith('loss_') && d.better)
  .map((d) => ({
    id: d.id,
    metric: d.metric,
    label: d.label,
    unit: d.unit,
    threshold: d.threshold,
    better: d.better as BetterDirection,
    headlineLoss: `${d.causeLoss.charAt(0).toUpperCase()}${d.causeLoss.slice(1)} is where most of the extra loss sits`,
    headlineGain: d.causeGain.charAt(0).toUpperCase() + d.causeGain.slice(1),
    recommendation: d.recommendation,
  }));
